import React, { useContext } from "react";

import { getDateTimestamp } from "@examspark/common";
import { toAWSDate } from "@examspark/common/dist/utils/coursePlanner";
import {
  SwitchButton,
  VSpace,
  Text,
  SelectInput,
  Label,
} from "@examspark/common-ui";
import { AwsDateInput } from "@examspark/common-ui/dist/components/inputs/DateInput";
import { unwrapSetStateAction } from "@examspark/common-ui/dist/utils/react";
import { ExamConfigData } from "@examspark/types";

import { LTIContext } from "../../providers/LTIProvider";

const ActivityPreferences = (props: {
  value: ExamConfigData;
  onChange?: (config: ExamConfigData) => void;
  showOptions?: {
    additionalContext?: boolean;
    shuffleItems?: boolean;
    showAttempts?: boolean;
    readAloud?: boolean;

    calculator?: boolean;
    highlighter?: boolean;
    lineReader?: boolean;
    optionEliminator?: boolean;
  };
}) => {
  const lti = useContext(LTIContext);

  const anyToolsEnabled =
    props.showOptions?.highlighter ||
    props.showOptions?.lineReader ||
    props.showOptions?.optionEliminator ||
    props.showOptions?.calculator;

  return (
    <>
      <VSpace size="0.5rem">
        <Text.H4>Administration Preferences</Text.H4>
        <VSpace size="0">
          <SwitchButton
            label="Shuffle answer choices (when possible)"
            checked={props.value.shuffleOptions || false}
            onClick={() =>
              props.onChange?.({
                ...props.value,
                shuffleOptions: !props.value.shuffleOptions,
              })
            }
          />
          {props.showOptions?.shuffleItems && (
            <SwitchButton
              label="Shuffle question order"
              checked={props.value.shuffleItems || false}
              onClick={() =>
                props.onChange?.({
                  ...props.value,
                  shuffleItems: !props.value.shuffleItems,
                })
              }
            />
          )}
          {props.showOptions?.readAloud && (
            <>
              <SwitchButton
                label="Enable Read Aloud (where available)"
                checked={!!props.value.enableReadAloud}
                onClick={() =>
                  props.onChange?.({
                    ...props.value,
                    enableReadAloud: !props.value.enableReadAloud,
                  })
                }
              />
            </>
          )}
          {props.showOptions?.additionalContext && (
            <SwitchButton
              label="Enable Content Clarifiers (where available)"
              checked={!!props.value.showAdditionalContext}
              onClick={() =>
                props.onChange?.({
                  ...props.value,
                  showAdditionalContext: !props.value.showAdditionalContext,
                })
              }
            />
          )}
        </VSpace>
      </VSpace>
      {anyToolsEnabled && (
        <VSpace size="0.5rem">
          <Text.H4>Tools & Supports</Text.H4>
          <VSpace size="0">
            {props.showOptions?.highlighter && (
              <SwitchButton
                label="Enable Highlighter"
                checked={props.value.enableHighlighter || false}
                onClick={() =>
                  props.onChange?.({
                    ...props.value,
                    enableHighlighter: !props.value.enableHighlighter,
                  })
                }
              />
            )}
            {props.showOptions?.lineReader && (
              <SwitchButton
                label="Enable Line Reader"
                checked={props.value.enableLineReader || false}
                onClick={() =>
                  props.onChange?.({
                    ...props.value,
                    enableLineReader: !props.value.enableLineReader,
                  })
                }
              />
            )}
            {props.showOptions?.optionEliminator && (
              <SwitchButton
                label="Enable Option Eliminator"
                checked={props.value.enableOptionEliminator || false}
                onClick={() =>
                  props.onChange?.({
                    ...props.value,
                    enableOptionEliminator: !props.value.enableOptionEliminator,
                  })
                }
              />
            )}
            {props.showOptions?.calculator && (
              <div style={{ marginTop: "1rem" }}>
                <Label label="Calculator Type">
                  <SelectInput
                    value={props.value.enableCalculatorType ?? "NONE"}
                    options={[
                      { value: "NONE", label: "None" },
                      { value: "BASIC", label: "Basic" },
                      { value: "SCIENTIFIC", label: "Scientific" },
                      { value: "GRAPHING", label: "Graphing" },
                    ]}
                    onChange={(e) => {
                      if (e != null) {
                        return props.onChange?.({
                          ...props.value,
                          enableCalculatorType:
                            e.value === "NONE" ? undefined : e.value,
                        });
                      }
                    }}
                  />
                </Label>
              </div>
            )}
          </VSpace>
        </VSpace>
      )}

      <VSpace size="0.5rem">
        <Text.H4>Upon Completion...</Text.H4>
        <VSpace size="0">
          <SwitchButton
            label="Reveal the student's score"
            checked={props.value.showScoreOnComplete || false}
            onClick={() =>
              props.onChange?.({
                ...props.value,
                showScoreOnComplete: !props.value.showScoreOnComplete,
              })
            }
          />
          <SwitchButton
            label="Reveal the correct answers"
            checked={props.value.showCorrectAnswersOnComplete || false}
            onClick={() =>
              props.onChange?.({
                ...props.value,
                showCorrectAnswersOnComplete:
                  !props.value.showCorrectAnswersOnComplete,
              })
            }
          />
          {/* <SwitchButton
            label="Offer links to remedial materials (if available)"
            disabled
            checked={props.value.showLearningReferencesOnComplete || false}
            onClick={() =>
              props.onChange?.({
                ...props.value,
                showLearningReferencesOnComplete:
                  !props.value.showLearningReferencesOnComplete,
              })
            }
          /> */}
        </VSpace>
      </VSpace>
      <VSpace size="0.5rem">
        <Text.H4>Retake Preferences</Text.H4>
        <VSpace size="0">
          <SwitchButton
            label="Allow re-takes"
            checked={props.value.allowRetakes || false}
            onClick={() =>
              props.onChange?.({
                ...props.value,
                allowRetakes: !props.value.allowRetakes,
              })
            }
          />
          {props.showOptions?.showAttempts && (
            <Label label="Score to Use">
              <SelectInput
                disabled={!props.value.allowRetakes}
                value={props.value.attemptResolutionStrategy?.mode ?? "LATEST"}
                options={[
                  { value: "LATEST", label: "Latest Score" },
                  { value: "HIGHEST", label: "Highest Score" },
                ]}
                onChange={(e) => {
                  if (e != null) {
                    return props.onChange?.({
                      ...props.value,
                      attemptResolutionStrategy: { mode: e.value },
                    });
                  }
                }}
              />
            </Label>
          )}
          {/* <SwitchButton
            label="Restrict number of re-takes (Coming Soon)"
            disabled
            checked={props.value.retakeLimitEnabled || false}
            onClick={() =>
              props.onChange?.({
                ...props.value,
                retakeLimitEnabled: !props.value.retakeLimitEnabled,
              })
            }
          />
          <div style={{ margin: "0.3rem 0 0.3rem 3.6rem" }}>
            <TextInput
              style={{ width: "10rem" }}
              maxLength={2}
              value={props.value.retakeLimit ?? ""}
              // disabled={!props.value.retakeLimitEnabled}
              disabled
              onChange={(e) =>
                props.onChange?.({
                  ...props.value,
                  retakeLimit: e.target.value,
                })
              }
            />
          </div> */}
        </VSpace>
      </VSpace>
      {!lti && (
        <VSpace size="0.5rem">
          <Text.H4>Expiration Preferences</Text.H4>
          <VSpace size="0">
            <SwitchButton
              label="Disable access before a specific date"
              checked={props.value.disableAccessBeforeEnabled || false}
              onClick={() =>
                props.onChange?.({
                  ...props.value,
                  disableAccessBeforeEnabled:
                    !props.value.disableAccessBeforeEnabled,
                  disableAccessBefore: !props.value.disableAccessBeforeEnabled
                    ? (props.value.disableAccessBefore ?? getDateTimestamp())
                    : (props.value.disableAccessBefore ?? undefined),
                })
              }
            />
            <div style={{ margin: "0.3rem 0 0.3rem 3.6rem" }}>
              <AwsDateInput
                disabled={!props.value.disableAccessBeforeEnabled}
                date={
                  props.value.disableAccessBefore
                    ? props.value.disableAccessBefore
                    : toAWSDate(new Date())
                }
                onDateChange={(value) => {
                  console.log(value);
                  props.onChange?.({
                    ...props.value,
                    disableAccessBefore: unwrapSetStateAction(
                      props.value.disableAccessBefore
                        ? props.value.disableAccessBefore
                        : toAWSDate(new Date()),
                      value,
                    ),
                  });
                }}
              />
            </div>
          </VSpace>
          <VSpace size="0">
            <SwitchButton
              label="Disable access on or after a specific date"
              checked={props.value.disableAccessOnOrAfterEnabled || false}
              onClick={() =>
                props.onChange?.({
                  ...props.value,
                  disableAccessOnOrAfterEnabled:
                    !props.value.disableAccessOnOrAfterEnabled,
                  disableAccessOnOrAfter: !props.value
                    .disableAccessOnOrAfterEnabled
                    ? (props.value.disableAccessOnOrAfter ?? getDateTimestamp())
                    : (props.value.disableAccessOnOrAfter ?? undefined),
                })
              }
            />
            <div style={{ margin: "0.3rem 0 0.3rem 3.6rem" }}>
              <AwsDateInput
                disabled={!props.value.disableAccessOnOrAfterEnabled}
                date={
                  props.value.disableAccessOnOrAfter
                    ? props.value.disableAccessOnOrAfter
                    : toAWSDate(new Date())
                }
                onDateChange={(value) => {
                  props.onChange?.({
                    ...props.value,
                    disableAccessOnOrAfter: unwrapSetStateAction(
                      props.value.disableAccessOnOrAfter
                        ? props.value.disableAccessOnOrAfter
                        : toAWSDate(new Date()),
                      value,
                    ),
                  });
                }}
              />
            </div>
          </VSpace>
        </VSpace>
      )}
    </>
  );
};

export default ActivityPreferences;
